import "../styles/globals.scss";
import type { AppProps } from "next/app";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Provider } from "react-redux";
import store, { useAppDispatch, useAppSelector } from "app/store/store";
import { Header } from "app/components/header/header.component";
import {
  QueryClient,
  QueryClientProvider,
  QueryErrorResetBoundary,
} from "react-query";
import { AuthUserProvider, useAuth } from "app/context/auth.context";
import Head from "next/head";
import { selectUserState } from "app/store/slices/user";
import {
  getAccountUsersMeContinuity,
  getPillowCategoryPath,
} from "services-hooks/services";
import { playerActions, selectPlayerState } from "app/store/slices/player";
import { Category, PodcastChapter } from "services-hooks/types";
import * as Sentry from "@sentry/nextjs";
import {
  ToastProvider,
  ToastRenderer,
  useErrorToast,
  useToast,
} from "app/components/toast/toast.component";
import { useArrowKeyNavigation } from "app/hooks/keyboard-navigation.hook";
import { Footer } from "app/components/footer/footer.component";
import {
  CountryContextProvider,
  LOCAL_STORAGE_LOCALE_OVERRIDE_KEY,
  useCountryContext,
} from "app/context/country.context";
import {
  appVersionActions,
  selectAppVersionState,
} from "app/store/slices/app-version";
import { radioActions } from "app/store/slices/radio";
import { userActions } from "app/store/slices/user";
import { useInterval, useNetworkState, useSessionStorage } from "react-use";
import {
  PlayerContextProvider,
  usePlayerContext,
  VLYBY_AD_ERROR_TIMEOUT,
} from "app/context/player.context";
import { NextIntlProvider } from "next-intl";
import { messages } from "app/locales";
import {
  FeaturesContextProvider,
  useFeaturesContext,
} from "app/context/features.context";
import { OfflineContextProvider } from "app/context/offline.context";
const queryClient = new QueryClient();
import "core-js/modules/es.string.replace-all";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "app/components/error-fallback/error-fallback.component";
import "scroll-behavior-polyfill";
import { useVirtualPlayPauseEvent } from "app/hooks/vkplay-pause.hook";
import "intersection-observer";
import { PreviouslyPlayed } from "app/components/previously-played/previously-played.component";
import { useTranslate } from "app/hooks/lang";
import { clearMediaSession } from "app/utils/media-session";
import { NavigationContextProvider } from "app/context/navigation.context";
import Script from "next/script";
import * as gtag from "app/utils/gtag";
import { useStickyState } from "app/hooks/sticky-state.hook";
import { VirtualKeyboard } from "app/components/virtual-keyboard/virtual-keyboard.component";
import { VirtualKeyboardContextProvider } from "app/context/virtual-keyboard.context";
import "@formatjs/intl-locale/polyfill";
import "@formatjs/intl-getcanonicallocales/polyfill";
import { VlybyContainer } from "app/components/station-player/vlyby-container.component";
import { timeout } from "app/ts";
import clsx from "clsx";
import { useRouter } from "next/router";
import { HISTORY_STACK_KEY } from "app/hooks/navigation.hook";

function MyApp({ Component, pageProps, router }: AppProps) {
  const [historyStack, setHistoryStack] = useSessionStorage<string[]>(
    HISTORY_STACK_KEY,
    []
  );
  useEffect(() => {
    const dir = router.locale == "ar" ? "rtl" : "ltr";
    document.querySelector("html")?.setAttribute("dir", dir);
  }, [router.locale]);

  const [localeOverride] = useStickyState<string | null>(
    LOCAL_STORAGE_LOCALE_OVERRIDE_KEY,
    null
  );
  const handleRouteChange = useCallback(
    (url: string) => {
      const newHistoryStack = [...historyStack];
      if (historyStack[historyStack.length - 1] !== url) {
        newHistoryStack.push(url);
      }
      setHistoryStack(newHistoryStack.slice(-3));
      gtag.pageview(url);
    },
    [historyStack]
  );
  useEffect(() => {
    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("hashChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("hashChangeComplete", handleRouteChange);
    };
  }, [router.events, handleRouteChange]);

  useEffect(() => {
    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }
  }, []);
  useEffect(() => {
    if (localeOverride !== null && router.locale !== localeOverride) {
      const { pathname, asPath, query } = router;
      router.replace({ pathname, query }, asPath, { locale: localeOverride });
    }
  }, [localeOverride, router.locale]);
  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width, viewport-fit=cover"
        />
        <meta content="html" />
        <link rel="apple-touch-icon" href="public/apple-icon-180.png" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2048-2732.jpg"
          media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2732-2048.jpg"
          media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1668-2388.jpg"
          media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2388-1668.jpg"
          media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1536-2048.jpg"
          media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2048-1536.jpg"
          media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1668-2224.jpg"
          media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2224-1668.jpg"
          media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1620-2160.jpg"
          media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2160-1620.jpg"
          media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1284-2778.jpg"
          media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2778-1284.jpg"
          media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1170-2532.jpg"
          media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2532-1170.jpg"
          media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1125-2436.jpg"
          media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2436-1125.jpg"
          media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1242-2688.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2688-1242.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-828-1792.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1792-828.jpg"
          media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1242-2208.jpg"
          media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-2208-1242.jpg"
          media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-750-1334.jpg"
          media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1334-750.jpg"
          media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-640-1136.jpg"
          media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
        />
        <link
          rel="apple-touch-startup-image"
          href="public/apple-splash-1136-640.jpg"
          media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
        />
      </Head>
      {gtag.GA_TRACKING_ID && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
          />
          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gtag.GA_TRACKING_ID}', {
            page_path: window.location.pathname,
          });
          `,
            }}
          />
        </>
      )}

      {/* CMP */}
      <Script
        id="sfbx-config"
        dangerouslySetInnerHTML={{
          __html: `
            const configSFBXAppConsent = {
              appKey: 'ea160580-4436-4b42-b9eb-964636634b92'
            }
            `,
        }}
      />
      <Script
        defer
        async
        src={"https://cdn.appconsent.io/tcf2-clear/current/core.bundle.js"}
      />
      <Script src="/consent-manager.js" />
      <Script
        id="sfbx-config"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            if (typeof gtag === 'function') {
              window.isGtagInitBeforeAppConsent = true
            } else {
              function gtag() {
                dataLayer.push(arguments);
              }
            }
            (window.adsbygoogle = window.adsbygoogle || []).pauseAdRequests = 1;
            gtag('consent', 'default', {
              'analytics_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'functionality_storage': 'denied',
              'security_storage': 'granted',
              'personalization_storage': 'denied',
              'ad_storage': 'denied',
              'wait_for_update': 500
            });
          `,
        }}
      />
      {/* CMP end */}

      <NextIntlProvider
        locale={
          localeOverride
            ? localeOverride
            : router.locale !== router.defaultLocale
            ? router.locale
            : "en"
        }
        key={
          localeOverride
            ? localeOverride
            : router.locale !== router.defaultLocale
            ? router.locale
            : "en"
        }
        messages={
          messages[
            (localeOverride
              ? localeOverride
              : router.locale !== router.defaultLocale
              ? router.locale
              : "en")! as keyof typeof messages
          ]
        }
      >
        <ToastProvider>
          <QueryClientProvider client={queryClient}>
            <QueryErrorResetBoundary>
              {({ reset }) => (
                <ErrorBoundary onReset={reset} fallbackRender={ErrorFallback}>
                  <Provider store={store}>
                    <OuterMyApp>
                      <AuthUserProvider>
                        <FeaturesContextProvider>
                          <OfflineContextProvider>
                            <PlayerContextProvider>
                              <CountryContextProvider>
                                <NavigationContextProvider>
                                  <VirtualKeyboardContextProvider>
                                    <InnerMyApp>
                                      <Header />
                                      <Component
                                        {...pageProps}
                                        key={router.route}
                                      />
                                      <ToastRenderer />
                                    </InnerMyApp>
                                  </VirtualKeyboardContextProvider>
                                </NavigationContextProvider>
                              </CountryContextProvider>
                            </PlayerContextProvider>
                          </OfflineContextProvider>
                        </FeaturesContextProvider>
                      </AuthUserProvider>
                    </OuterMyApp>
                  </Provider>
                </ErrorBoundary>
              )}
            </QueryErrorResetBoundary>
          </QueryClientProvider>
        </ToastProvider>
      </NextIntlProvider>
      <script
        async
        defer
        src="https://play.adtonos.com/attc-3Kx24kdRpatbdYQGN.min.js"
      ></script>
    </>
  );
}
const OuterMyApp: React.FC = ({ children }) => {
  const { version } = useAppSelector(selectAppVersionState);
  const dispatch = useAppDispatch();

  const resetAndReload = () => {
    dispatch(playerActions.reset());
    dispatch(radioActions.reset());
    dispatch(userActions.reset());

    window.location.reload();
  };

  useEffect(() => {
    if (version !== process.env.packageVersion) {
      dispatch(playerActions.reset());
      dispatch(radioActions.reset());
    }

    dispatch(
      appVersionActions.setAppVersion({
        version: process.env.packageVersion || null,
      })
    );
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        resetAndReload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

const InnerMyApp: React.FC = (props) => {
  const { session } = useAppSelector(selectUserState);
  const { activeMedia } = useAppSelector(selectPlayerState);
  const { previouslyPlaying } = useAppSelector(selectUserState);
  const { setIsMediaLoading, setPlayerEnabled, setIsPlaying, isPlaying } =
    usePlayerContext();
  const [continuityLoaded, setContinuityLoaded] = useState(false);
  const {
    showFooter,
    arrowNavigationEnabled,
    customVirtualKeyboardEnabled,
    forceAutoplay,
    hideScrollbars,
    vlybyPublisherId,
    premiumVersion,
    isHTTPModeEnabled,
  } = useFeaturesContext();

  useArrowKeyNavigation(arrowNavigationEnabled);
  const dispatch = useAppDispatch();
  const networkState = useNetworkState();
  const [slowConnectionDetected, setSlowConnectionDetected] = useState(false);
  const t = useTranslate();
  const router = useRouter();

  const showOfflineError = useErrorToast({
    content: t("network.connection-lost"),
  });
  const showSlowNetworkError = useToast({
    content: t("network.connection-slow"),
  });
  const showOnlineToast = useToast({
    content: t("network.back-online"),
  });
  useEffect(() => {
    if (!networkState.online) {
      showOfflineError();
    }
  }, [networkState.online]);
  useEffect(() => {
    if (networkState.online && networkState.previous === false) {
      showOnlineToast();
    }
  }, [networkState.online, networkState.previous]);

  useEffect(() => {
    if (
      !slowConnectionDetected &&
      networkState.effectiveType &&
      ["2g", "slow-2g"].includes(networkState.effectiveType)
    ) {
      showSlowNetworkError();
      setSlowConnectionDetected(true);
    }
    if (
      slowConnectionDetected &&
      networkState.effectiveType &&
      ["4g", "3g"].includes(networkState.effectiveType)
    ) {
      setSlowConnectionDetected(false);
    }
  }, [slowConnectionDetected, networkState.effectiveType]);

  useEffect(() => {
    if (forceAutoplay) {
      setPlayerEnabled(true);
    }
    setIsMediaLoading(false);
  }, [forceAutoplay]);

  useEffect(() => {
    if (hideScrollbars && document && document.body) {
      document.body.classList.add("hide_scrollbars");
      document.body.parentElement?.classList.add("hide_scrollbars");
    }
  }, [hideScrollbars]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const continuityResponse = await getAccountUsersMeContinuity();
        if (continuityResponse.data.body?.content?.listen) {
          const result = await getPillowCategoryPath(
            continuityResponse.data.body?.content?.listen,
            {}
          );
          const continuityCategory = result.data.body?.content as Category;
          if (continuityCategory) {
            if (continuityCategory?.type === "radio") {
              if (
                activeMedia?.category?.permalink !==
                  continuityCategory.permalink &&
                previouslyPlaying?.media?.category?.permalink !==
                  continuityCategory.permalink
              ) {
                dispatch(
                  userActions.setPreviouslyPlaying({
                    dismissed: false,
                    media: {
                      podcast: null,
                      category: continuityCategory,
                    },
                  })
                );
              }
            } else if (continuityCategory.type === "chapter") {
              const isDifferentThanActive =
                activeMedia?.podcast?.podcastChapter.permalink !==
                continuityCategory.permalink;
              const isDifferentThanDismissed =
                previouslyPlaying?.media?.podcast?.podcastChapter !==
                continuityCategory.permalink;
              if (isDifferentThanActive && isDifferentThanDismissed) {
                const result = await getPillowCategoryPath(
                  (continuityCategory as PodcastChapter).podcastPermalink!,
                  {}
                );
                if (result.data.body?.content) {
                  dispatch(
                    userActions.setPreviouslyPlaying({
                      dismissed: false,
                      media: {
                        podcast: {
                          podcast: result.data.body?.content as Category,
                          podcastChapter: continuityCategory,
                        },
                        category: null,
                      },
                    })
                  );
                }
              }
            }
          }
        }
        setContinuityLoaded(true);
      } catch (e) {
        Sentry.captureException(e);
        console.error("Could not play last played item");
      }
    };
    if (session?.user?.id && !continuityLoaded) {
      fetchData();
    }
  }, [session?.user?.id, continuityLoaded]);

  useVirtualPlayPauseEvent(
    () => {
      setIsPlaying(true);
    },
    () => {
      setIsPlaying(false);
    },
    () => {
      setIsPlaying(!isPlaying);
    },
    () => {
      setIsPlaying(false);
      // reset player completely
      dispatch(playerActions.reset());
      dispatch(radioActions.reset());
    },
    arrowNavigationEnabled
  );
  useEffect(() => {
    if (!activeMedia) {
      clearMediaSession();
    }
  }, [activeMedia]);

  // HTTP mode is only allowed on the /player/[permalink] page.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.protocol === "http:" && !router.pathname.startsWith("/player/") && isHTTPModeEnabled) {
      window.location.href = `https://${window.location.host}/`;
      // window.location.href = `https://localhost:3001/`; // test
    }
  }, []);

  const [enableVlyby, setEnableVlyby] = useState(!!vlybyPublisherId);
  const [videoAdActive, setVideoAdActive] = useState(false);
  const [closedAdTimeout, setClosedAdTimeout] = useState(false);
  const [isAdblockDetected, setIsAdblockDetected] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(false);
  const { country } = useCountryContext();
  const timeoutId = useRef<any>();
  const adHideTimeoutId = useRef<any>();
  const getIsPageVisible = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.document.visibilityState === "visible";
    }
    return true;
  }, []);

  const closeVideoAds = () => {
    stopVlybyVideoAds();
    setEnableVlyby(false);
    setClosedAdTimeout(true);
    setVideoAdActive(false);
    timeoutId.current = setTimeout(() => {
      setClosedAdTimeout(false);
      setEnableVlyby(!!vlybyPublisherId);
    }, 5 * 60000);
  };

  useEffect(() => {
    const onblur = () => {
      setIsPageVisible(window.document.visibilityState === "visible");
    };
    const onfocus = () => {
      setIsPageVisible(window.document.visibilityState === "visible");
    };
    const onvisibilitychange = () => {
      setIsPageVisible(window.document.visibilityState === "visible");
    };

    try {
      if (typeof window !== "undefined") {
        (window as any).mainVlybyAdClosedByUser = function () {
          closeVideoAds();
          (window as any).mainIsVlybyAdReady = false;
        };
        (window as any).mainHideVlybyPlayer = function () {
          stopVlybyVideoAds();

          timeoutId.current = setTimeout(() => {
            setEnableVlyby(!!vlybyPublisherId);
          }, 10000);
        };
        (window as any).mainVlybyAdReady = function () {
          (window as any).mainIsVlybyAdReady = true;
          clearTimeout(timeoutId.current);
          clearTimeout(adHideTimeoutId.current);
          setVideoAdActive(true);
        };

        window.addEventListener("blur", onblur);
        window.addEventListener("focus", onfocus);
        if (typeof window.document.addEventListener !== "undefined") {
          window.document.addEventListener(
            "visibilitychange",
            onvisibilitychange
          );
        }
      }
    } catch (error) {}

    return () => {
      try {
        if (typeof window !== "undefined") {
          delete (window as any).mainHideVlybyPlayer;
          delete (window as any).mainVlybyAdReady;
          delete (window as any).mainIsVlybyAdReady;

          window.removeEventListener("blur", onblur);
          window.removeEventListener("focus", onfocus);
          if (typeof window.document.addEventListener !== "undefined") {
            window.removeEventListener("visibilitychange", onvisibilitychange);
          }
        }
        clearTimeout(timeoutId.current);
      } catch (error) {}
    };
  }, []);

  // test adblock
  useEffect(() => {
    if (typeof window !== "undefined") {
      const adBoxEl = window.document.querySelector(".ad-box");
      const hasAdBlock =
        !!adBoxEl && window.getComputedStyle(adBoxEl)?.display === "none";

      setIsAdblockDetected(hasAdBlock);
    }
  }, []);

  const showVlyby = useMemo(
    () =>
      enableVlyby &&
      getIsPageVisible() &&
      country.countryCode !== "de" &&
      !premiumVersion &&
      !session?.user?.premium &&
      !router.pathname.includes("settings") &&
      !router.pathname.includes("log-in") &&
      !router.pathname.includes("get-premium") &&
      !router.pathname.includes("contact-us") &&
      !router.pathname.includes("terms-of-use") &&
      !router.pathname.includes("privacy-policy") &&
      !isAdblockDetected,
    [
      enableVlyby,
      session?.user?.premium,
      router.pathname,
      isAdblockDetected,
      isPageVisible,
    ]
  );

  const activeCategory = activeMedia?.category;

  const hideTimeout = useMemo(() => {
    if (networkState.effectiveType) {
      if (["2g", "slow-2g"].includes(networkState.effectiveType)) {
        return 30000;
      }
      if (["3g"].includes(networkState.effectiveType)) {
        return 20000;
      }
    }
    return 10000;
  }, [networkState]);

  const stopVlybyVideoAds = async () => {
    clearTimeout(timeoutId.current);
    clearTimeout(adHideTimeoutId.current);
    setVideoAdActive(false);
    setEnableVlyby(false);

    (window as any).mainIsVlybyAdReady = false;
  };

  const startLoadingVlyby = async () => {
    if (!videoAdActive && !closedAdTimeout) {
      setEnableVlyby(!!vlybyPublisherId);

      clearTimeout(timeoutId.current);
      clearTimeout(adHideTimeoutId.current);
      timeoutId.current = setTimeout(() => {
        if (typeof window !== "undefined") {
          if (!(window as any).mainIsVlybyAdReady) {
            stopVlybyVideoAds();
            adHideTimeoutId.current = setTimeout(() => {
              setEnableVlyby(!!vlybyPublisherId);
            }, VLYBY_AD_ERROR_TIMEOUT);
          }
        }
      }, hideTimeout);
    }
  };

  useEffect(() => {
    if (enableVlyby) {
      startLoadingVlyby();
    }
  }, [enableVlyby]);

  return (
    <>
      {props.children}
      <PreviouslyPlayed />
      {arrowNavigationEnabled && customVirtualKeyboardEnabled && (
        <VirtualKeyboard />
      )}
      <Footer showFooter={showFooter} />
      <div
        className="ad-box"
        style={{ position: "fixed", top: 0, left: 0 }}
        aria-hidden={true}
      >
        &nbsp;
      </div>
      {showVlyby && (
        <div
          className={clsx("vlyby_container_main", {
            vlyby_mini_player_active: !!activeCategory,
          })}
        >
          <div className="vlyby_container_main_inner">
            <div className="vlyby_container_main_content">
              <VlybyContainer
                width={"100%"}
                height={"100%"}
                variant="floating"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyApp;
